import React, { useCallback, useState, useRef } from "react";

export interface Dimension {
  width: number;
  height: number;
}

interface ResizeHandleProps {
  isVisible: boolean;
  size: Dimension;
  onResizeChange: (dimensions: { width: number; height: number }) => void;
  onResizeEnd: (dimensions: { width: number; height: number }) => void;
}

const ResizeHandle = ({
  size,
  onResizeChange,
  onResizeEnd,
  isVisible,
}: ResizeHandleProps) => {
  const isResizing = useRef<boolean>(false);
  const initialMousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialDimensions = useRef<Dimension>({
    width: size.width,
    height: size.height,
  });

  const startResizing = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();
      e.preventDefault();
      isResizing.current = true;
      initialMousePos.current = { x: e.clientX, y: e.clientY };
      initialDimensions.current = { ...size };

      const handleMouseMove = (moveEvent: MouseEvent): void => {
        if (!isResizing.current) return;
        const deltaX: number = moveEvent.clientX - initialMousePos.current.x;
        const deltaY: number = moveEvent.clientY - initialMousePos.current.y;
        const newWidth: number = Math.max(
          100,
          initialDimensions.current.width + deltaX,
        );
        const newHeight: number = Math.max(
          50,
          initialDimensions.current.height + deltaY,
        );
        const newDimension = { width: newWidth, height: newHeight };
        onResizeChange(newDimension);
      };

      const handleMouseUp = (): void => {
        isResizing.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        onResizeEnd({ width: size.width, height: size.height });
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [size],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`absolute bottom-0 right-0 w-[16px] h-[16px] cursor-nwse-resize bg-transparent bg-[rgba(1,1,1,0.1)] rounded-br-lg`}
      onMouseDown={startResizing}
    />
  );
};

export default ResizeHandle;
